import { defineList, type FieldsOf } from "./define";

/**
 * Schema dell'app "Acquisti" (sostituisce le app Power Apps Acquisti_utente e Acquisti_Gestore).
 * Nomi interni ASCII senza accenti; le etichette visibili possono averli.
 *
 * Elenchi della vecchia app da NON toccare: Richieste acquisti, Richieste stampanti,
 * ordinabili, Codici ordinabili, Stampanti, Gestori. Per questo i titoli qui sotto
 * sono diversi da quelli esistenti.
 */

/* -------------------------------------------------------------------------- */
/* Valori di riferimento                                                       */
/* -------------------------------------------------------------------------- */

export const TIPI = ["Cancelleria", "Mensa", "Stampanti", "Generica"] as const;
export type Tipo = (typeof TIPI)[number];

export const STATI = ["Sottomessa", "Presa visione", "In lavorazione", "Bloccata", "Respinta", "Evasa"] as const;
export type Stato = (typeof STATI)[number];

/** Stati che chiudono la richiesta (valorizzano DataChiusura). */
export const STATI_CHIUSI: readonly Stato[] = ["Evasa", "Respinta"];

export const PRIORITA = ["Standard", "Urgente"] as const;

/* -------------------------------------------------------------------------- */
/* Elenchi di configurazione                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Condiviso con Manutenzioni: stessa definizione, già presente sul sito.
 * Il provisioning lo trova uguale e non lo modifica.
 */
export const Servizi = defineList({
  key: "servizi",
  displayName: "Servizi",
  description: "Servizi tra cui scegliere nelle richieste di manutenzione (gestito dai gestori)",
  template: "genericList",
  titleLabel: "Servizio",
  columns: [
    { kind: "boolean", name: "Attivo", displayName: "Attivo", defaultValue: true },
    { kind: "number", name: "Ordine", displayName: "Ordine", decimalPlaces: "none" },
  ],
  // Inseriti solo se l'elenco è vuoto (sul sito di Manutenzioni è già popolato)
  seed: [
    { Title: "CDD", Attivo: true, Ordine: 10 },
    { Title: "CSE", Attivo: true, Ordine: 20 },
    { Title: "SFA/Accademia", Attivo: true, Ordine: 30 },
    { Title: "Housing", Attivo: true, Ordine: 40 },
    { Title: "Bistrozzi", Attivo: true, Ordine: 50 },
    { Title: "Comunità", Attivo: true, Ordine: 60 },
    { Title: "Diurno Minori", Attivo: true, Ordine: 70 },
    { Title: "Uffici", Attivo: true, Ordine: 80 },
  ],
});
export type ServizioFields = FieldsOf<typeof Servizi>;

/** Categorie per i tipi Cancelleria, Mensa e Generica (Generica le mostra tutte). */
export const CategorieAcquisto = defineList({
  key: "categorie",
  displayName: "Categorie acquisto",
  description: "Categorie di articoli richiedibili, per tipo di richiesta (gestito dai gestori)",
  template: "genericList",
  titleLabel: "Categoria",
  columns: [
    { kind: "choice", name: "Tipo", displayName: "Tipo", choices: TIPI, required: true },
    { kind: "boolean", name: "Attiva", displayName: "Attiva", defaultValue: true },
    { kind: "number", name: "Ordine", displayName: "Ordine", decimalPlaces: "none" },
  ],
  seed: [
    { Title: "Carta", Tipo: "Cancelleria", Attiva: true, Ordine: 10 },
    { Title: "Biro", Tipo: "Cancelleria", Attiva: true, Ordine: 20 },
    { Title: "Matite", Tipo: "Cancelleria", Attiva: true, Ordine: 30 },
    { Title: "Faldoni", Tipo: "Cancelleria", Attiva: true, Ordine: 40 },
    { Title: "Cartelline", Tipo: "Cancelleria", Attiva: true, Ordine: 50 },
    { Title: "Tovagliette", Tipo: "Mensa", Attiva: true, Ordine: 10 },
    { Title: "Tovaglioli", Tipo: "Mensa", Attiva: true, Ordine: 20 },
    { Title: "Posate", Tipo: "Mensa", Attiva: true, Ordine: 30 },
    { Title: "Bicchieri", Tipo: "Mensa", Attiva: true, Ordine: 40 },
    { Title: "Acqua", Tipo: "Mensa", Attiva: true, Ordine: 50 },
  ],
});
export type CategoriaFields = FieldsOf<typeof CategorieAcquisto>;

/**
 * Chi gestisce cosa: una riga per coppia gestore–tipo.
 * Decide chi riceve la notifica di nuova richiesta e il filtro iniziale della coda.
 * NON concede permessi: per quelli serve il gruppo "Gestori acquisti".
 */
export const GestoriAcquisti = defineList({
  key: "gestori",
  displayName: "Gestori acquisti",
  description: "Assegnazione dei tipi di richiesta ai gestori (notifiche e filtro predefinito)",
  template: "genericList",
  titleLabel: "Nota",
  columns: [
    { kind: "person", name: "Gestore", displayName: "Gestore", required: true },
    { kind: "choice", name: "Tipo", displayName: "Tipo", choices: TIPI, required: true },
    { kind: "boolean", name: "Notifiche", displayName: "Riceve notifiche", defaultValue: true },
  ],
});
export type GestoreFields = FieldsOf<typeof GestoriAcquisti>;

/* -------------------------------------------------------------------------- */
/* Catalogo materiali per stampanti                                            */
/* -------------------------------------------------------------------------- */

export const Stampanti = defineList({
  key: "stampanti",
  displayName: "Stampanti in uso",
  description: "Stampanti per cui si possono ordinare consumabili",
  template: "genericList",
  titleLabel: "Stampante",
  columns: [
    { kind: "text", name: "Modello", displayName: "Modello" },
    { kind: "text", name: "Sede", displayName: "Sede / ubicazione" },
    { kind: "text", name: "Servizio", displayName: "Servizio", description: "Valore preso dall'elenco Servizi" },
    { kind: "boolean", name: "Attiva", displayName: "Attiva", defaultValue: true },
    { kind: "note", name: "Note", displayName: "Note" },
  ],
});
export type StampanteFields = FieldsOf<typeof Stampanti>;

export const Articoli = defineList({
  key: "articoli",
  displayName: "Articoli stampanti",
  description: "Consumabili ordinabili (toner, tamburi, cartucce) con le stampanti compatibili",
  template: "genericList",
  titleLabel: "Articolo",
  columns: [
    { kind: "text", name: "Codice", displayName: "Codice fornitore", indexed: true },
    { kind: "note", name: "Descrizione", displayName: "Descrizione" },
    { kind: "text", name: "Unita", displayName: "Unità di vendita", maxLength: 20 },
    { kind: "currency", name: "Prezzo", displayName: "Prezzo di listino (IVA esclusa)", locale: "it-IT" },
    { kind: "number", name: "Sconto", displayName: "Sconto %", decimalPlaces: "two", min: 0, max: 100 },
    { kind: "number", name: "Iva", displayName: "IVA %", decimalPlaces: "none", min: 0, max: 100 },
    { kind: "lookup", name: "Stampanti", displayName: "Stampanti compatibili", list: "stampanti", allowMultiple: true },
    { kind: "boolean", name: "Attivo", displayName: "Attivo", defaultValue: true },
  ],
});
export type ArticoloFields = FieldsOf<typeof Articoli>;

/* -------------------------------------------------------------------------- */
/* Richieste                                                                   */
/* -------------------------------------------------------------------------- */

export const RichiesteAcquisto = defineList({
  key: "richieste",
  // Titolo diverso da "Richieste acquisti" della vecchia app, che resta finché non viene dismessa.
  displayName: "Richieste di acquisto",
  description: "Richieste di acquisto inviate dai coordinatori (un articolo per richiesta)",
  template: "genericList",
  // Riassunto generato dall'app: nome dell'articolo, oppure l'inizio della descrizione
  titleLabel: "Titolo",
  indexBuiltIn: ["Created"],
  columns: [
    // --- compilati dal richiedente ---
    { kind: "choice", name: "Tipo", displayName: "Tipo", choices: TIPI, required: true, indexed: true },
    {
      kind: "text",
      name: "Servizio",
      displayName: "Servizio",
      description: "Valore preso dall'elenco Servizi",
      required: true,
      indexed: true,
    },
    { kind: "text", name: "Categoria", displayName: "Categoria", description: "Dall'elenco Categorie acquisto (non per Stampanti)" },
    { kind: "lookup", name: "Stampante", displayName: "Stampante", list: "stampanti" },
    { kind: "lookup", name: "Articolo", displayName: "Articolo", list: "articoli" },
    { kind: "note", name: "Descrizione", displayName: "Descrizione", required: true },
    { kind: "number", name: "Quantita", displayName: "Quantità", decimalPlaces: "automatic", min: 1, required: true },
    {
      kind: "choice",
      name: "Priorita",
      displayName: "Priorità",
      choices: PRIORITA,
      defaultValue: "Standard",
      indexed: true,
    },
    { kind: "note", name: "Note", displayName: "Note del richiedente" },
    // --- automatici ---
    { kind: "person", name: "Richiedente", displayName: "Richiedente", indexed: true },
    { kind: "text", name: "Codice", displayName: "Codice articolo", description: "Copiato dal catalogo al momento della richiesta" },
    {
      kind: "currency",
      name: "PrezzoUnitario",
      displayName: "Prezzo unitario",
      locale: "it-IT",
      description: "Copiato dal catalogo: prezzo scontato, IVA esclusa",
    },
    { kind: "dateTime", name: "DataChiusura", displayName: "Data chiusura", format: "dateTime" },
    { kind: "person", name: "GestitoDa", displayName: "Gestito da" },
    // --- compilati dal gestore ---
    {
      kind: "choice",
      name: "Stato",
      displayName: "Stato",
      choices: STATI,
      defaultValue: "Sottomessa",
      required: true,
      indexed: true,
    },
    {
      kind: "currency",
      name: "Valore",
      displayName: "Valore",
      locale: "it-IT",
      description: "IVA esclusa. Per le stampanti proposto come quantità × prezzo unitario; il gestore lo conferma o corregge",
    },
    { kind: "boolean", name: "ExtraBudget", displayName: "Extra budget", defaultValue: false, indexed: true },
    { kind: "note", name: "NoteGestore", displayName: "Note gestore" },
  ],
});
export type RichiestaFields = FieldsOf<typeof RichiesteAcquisto>;

/** Tutte le strutture dati dell'app, nell'ordine di provisioning (i lookup dopo i loro elenchi). */
export const appSchema = [Servizi, CategorieAcquisto, GestoriAcquisti, Stampanti, Articoli, RichiesteAcquisto] as const;
